// import { Modal, Button } from "antd";
// import { useState } from "react";
// import OTPInput from "react-otp-input";
// import { useDispatch, useSelector } from "react-redux";
// import { AppAction } from "../../store/actions";
// import { Utils } from "../../config";
// import { useDarkModeValue } from "../../hooks/useDarkModeValue";

// export const AppLockModal = () => {
//   const [passcode, setPasscode] = useState("");
//   const dispatch = useDispatch();
//   const { unlockLoader } = useSelector((state) => state.AppReducer);

//   const { isDarkMode, colors } = useDarkModeValue();

//   const handleUnlock = () => {
//     dispatch(
//       AppAction.VerifyPasscode({ passcode }, () => {
//         dispatch(AppAction.AppUnlock());
//         setPasscode("");
//         Utils.showMessage("success", "App unlocked successfully");
//       }),
//     );
//   };

//   return (
//     <Modal
//       open={true}
//       closable={false}
//       centered
//       footer={null}
//       bodyStyle={{
//         padding: "2rem",
//         textAlign: "center",
//         borderRadius: "0.5rem",
//       }}
//       styles={{
//         mask: {
//           backdropFilter: "blur(10px)",
//           backgroundColor: "rgba(0, 0, 0, 0.8)",
//         },
//         content: {
//           backgroundColor: colors.White,
//         },
//       }}
//     >
//       <h2 className="text-xl font-semibold mb-2">Enter Passcode</h2>
//       <p className="text-sm text-gray-500 mb-4">
//         Please Enter your 4-digit PIN to unlock the app
//       </p>
//       <OTPInput
//         value={passcode}
//         onChange={setPasscode}
//         numInputs={4}
//         containerStyle={{ gap: "12px", justifyContent: "center" }}
//         renderInput={(props) => (
//           <input
//             {...props}
//             type="password"
//             inputMode="numeric"
//             className="otp-input"
//             style={{
//               width: "52px",
//               height: "52px",
//               borderRadius: "10px",
//               border: `1px solid ${colors.borderColor}`,
//               fontSize: "1.2rem",
//               textAlign: "center",
//               color: colors.Black,
//               backgroundColor: !isDarkMode ? colors.White : "var(--panel)",
//             }}
//           />
//         )}
//       />

//       <Button
//         type="primary"
//         className="mt-4 w-full bg-red-400 hover:bg-red-500 disabled:text-white"
//         onClick={handleUnlock}
//         disabled={passcode.length < 4}
//         loading={unlockLoader}
//       >
//         Unlock
//       </Button>
//     </Modal>
//   );
// };

import { Modal, Button } from 'antd';
import { useEffect, useState } from 'react';
import OTPInput from 'react-otp-input';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { AppAction } from '../../store/actions';
import { useDarkModeValue } from '../../hooks/useDarkModeValue';
import { useShake } from '../../hooks/useShake';

export const AppLockModal = () => {
  const [passcode, setPasscode] = useState('');
  const dispatch = useDispatch();
  const { unlockLoader, authRejections } = useSelector((state) => state.AppReducer, shallowEqual);

  const { isDarkMode, colors } = useDarkModeValue();
  const { shakeProps } = useShake(authRejections);

  // Clear the boxes on a rejection, so the shake is not followed by having to
  // delete four digits by hand before trying again.
  useEffect(() => {
    if (authRejections > 0) setPasscode('');
  }, [authRejections]);

  const handleUnlock = (event) => {
    // Called both by the button (as a submit) and by Enter in the PIN row.
    event?.preventDefault?.();
    if (passcode.length < 4) return;

    dispatch(
      // No toast: the lock screen going away is the confirmation, and a
      // notification about it outlives the screen it refers to.
      AppAction.VerifyPasscode({ passcode }, () => {
        dispatch(AppAction.AppUnlock());
        setPasscode('');
      })
    );
  };

  return (
    <Modal
      open={true}
      closable={false}
      centered
      footer={null}
      bodyStyle={{
        padding: '2rem',
        textAlign: 'center',
        borderRadius: '0.5rem',
      }}
      styles={{
        mask: {
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        },
        content: {
          backgroundColor: colors.White,
        },
      }}
    >
      {/* A form, so the fourth digit followed by Enter unlocks — the PIN row had
          no submit path at all and the button had to be found and clicked. */}
      <form onSubmit={handleUnlock}>
        <h2 className="text-xl font-semibold mb-2 dark:text-white">Enter Passcode</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Please Enter your 4-digit PIN to unlock the app
        </p>
        {/* Shakes on a wrong PIN — the rejection used to be reported only by a
          toast in the corner, while the four boxes sat there unchanged. Passcode
          is cleared with it so the next attempt starts from an empty row. */}
        <div {...shakeProps}>
          <OTPInput
            value={passcode}
            onChange={setPasscode}
            numInputs={4}
            containerStyle={{ gap: '12px', justifyContent: 'center' }}
            renderInput={(props) => (
              <input
                {...props}
                type="password"
                inputMode="numeric"
                className="otp-input"
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '10px',
                  border: `1px solid ${colors.borderColor}`,
                  fontSize: '1.2rem',
                  textAlign: 'center',
                  // Always white text on dark input background
                  color: isDarkMode ? '#ffffff' : '#000000',
                  backgroundColor: isDarkMode ? 'var(--panel)' : colors.White,
                }}
              />
            )}
          />
        </div>

        <Button
          type="primary"
          // Submits the form above, so Enter and the click share one path.
          htmlType="submit"
          className="mt-4 w-full bg-red-400 hover:bg-red-500"
          disabled={passcode.length < 4}
          loading={unlockLoader}
          style={{
            // Force white text always — including disabled state
            color: '#ffffff',
            opacity: passcode.length < 4 ? 0.6 : 1,
            // Prevent Ant Design from overriding text color on disabled
            ...(passcode.length < 4 && {
              backgroundColor: isDarkMode ? '#f87171' : '#f87171',
              borderColor: 'transparent',
            }),
          }}
        >
          Unlock
        </Button>
      </form>
    </Modal>
  );
};
